import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { logout } from "./userSlice";
import { GET } from "@/services/api";
import { API_ROUTES } from "@/services/environment";

// Define the BillingDetails interface
interface BillingDetails {
  next_billing_date?: string |null;
  subscription_status: "active" | "canceled" | "past_due"|"trialing" | null;
  card_status?: boolean |null;
  upcoming_amount?: number |null;
  cancel_at_period_end?: boolean | null;
  future_payment?: string | number | null;
  error?: string | null;
  card_number?: string |null;
  card_brand?: string |null;
  card_expiry_date?: string |null;
}

// Define the state interface
interface BillingState {
  billingDetails: BillingDetails ;
  error: string | null; 
}

const initialState: BillingState = {
  billingDetails: {
    next_billing_date: "",
    subscription_status: "active",
    card_status: false,
    upcoming_amount: 0,
    cancel_at_period_end: false,
    future_payment: null,
    error: null,
    card_number: "",
    card_brand: "",
    card_expiry_date: "",
  },
  error: null,
};
  const { billingDetailsApi } = API_ROUTES;

export const fetchBillingInfo = createAsyncThunk(
  "billingInfo/fetchBillingInfo",
  async (
    { businessId, refreshToken }: { businessId: string; refreshToken: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await GET(
        `${billingDetailsApi}/${businessId}`,
        refreshToken
      );
      return response.data[0];
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch billing info");
    }
  }
);

const billingInfoSlice = createSlice({
  name: "billingInfo",
  initialState,
  reducers: {
    activateSubscription: (state) => {
      if (state.billingDetails) {
        state.billingDetails.cancel_at_period_end = false;
        state.billingDetails.subscription_status = "active";

      }
    },
    cancelSubscription: (state) => {
      if (state.billingDetails) {
        state.billingDetails.cancel_at_period_end = true;
      }
    },
    updateCardDetails: (
      state,
      action: PayloadAction<{
        card_brand: string;
        card_expiry_date: string;
        card_number: string;
      }>
    ) => {
        state.billingDetails.card_brand = action.payload.card_brand;
        state.billingDetails.card_expiry_date = action.payload.card_expiry_date;
        state.billingDetails.card_number = action.payload.card_number;
    },
    updateBillingInfo:(state,{payload})=>{
      state.billingDetails = payload
    },
    resetBilling: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillingInfo.pending, (state) => {
        if (state.billingDetails) {
          state.billingDetails.error = null;
        }
        state.error = null;
      })
      .addCase(fetchBillingInfo.fulfilled, (state, action: PayloadAction<BillingDetails>) => {
        state.billingDetails = action.payload;
        state.error = null;
      })
      .addCase(fetchBillingInfo.rejected, (state, action: PayloadAction<unknown>) => {
        state.error = action.payload as string;
      })
      .addCase(logout, (state) => {
        state.billingDetails = {
          next_billing_date: null,
          subscription_status: null,
          card_status: false,
          upcoming_amount: null,
          cancel_at_period_end: null,
          future_payment: null,
          error: null,
          card_number: null,
          card_brand: null,
          card_expiry_date: null}
      }); 
  },
});

// Export the reducer
export default billingInfoSlice.reducer;

// Export actions
export const {
  activateSubscription,
  cancelSubscription,
  updateBillingInfo,
  updateCardDetails,
  resetBilling,
} = billingInfoSlice.actions;
